import { buildApiUrl } from "../../config";
import { withCredentials } from "../apiClient";
import { fetchWithRetry, readModelError } from "../../utils";
import { POWERPOINT_NARRATOR_STREAM_SYSTEM_PROMPT } from "./promptCatalog";
import type { NarratorPromptPayload } from "./promptCatalog";

interface OpenAIResponsesStreamEvent {
  type?: string;
  delta?: string;
  response?: {
    error?: {
      message?: string;
    };
  };
  error?: {
    message?: string;
  };
}

export interface NarratorStreamRequest {
  model: string;
  payload: NarratorPromptPayload;
  signal?: AbortSignal;
  onDelta: (delta: string) => void;
}

export type NarratorStreamClient = (request: NarratorStreamRequest) => Promise<void>;

const NARRATOR_STREAM_TIMEOUT_MS = 15_000;

function createTimeoutSignal(signal?: AbortSignal): {
  signal?: AbortSignal;
  cleanup: () => void;
} {
  if (typeof AbortController === "undefined") {
    return { signal, cleanup: () => {} };
  }

  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const abort = () => controller.abort();

  if (signal?.aborted) {
    controller.abort();
  } else if (signal) {
    signal.addEventListener("abort", abort, { once: true });
  }

  timeoutId = setTimeout(() => controller.abort(), NARRATOR_STREAM_TIMEOUT_MS);

  return {
    signal: controller.signal,
    cleanup: () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (signal) signal.removeEventListener("abort", abort);
    },
  };
}

function extractTextDelta(event: OpenAIResponsesStreamEvent): string {
  if (event.type === "response.output_text.delta" && typeof event.delta === "string") {
    return event.delta;
  }
  if (event.type === "response.refusal.delta" && typeof event.delta === "string") {
    return event.delta;
  }
  return "";
}

async function readResponsesTextStream(
  body: ReadableStream<Uint8Array>,
  onDelta: (delta: string) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamError = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(":") || trimmed.startsWith("event:")) continue;
        if (!trimmed.startsWith("data:")) continue;

        const dataStr = trimmed.slice(5).trim();
        if (!dataStr || dataStr === "[DONE]") continue;

        let event: OpenAIResponsesStreamEvent;
        try {
          event = JSON.parse(dataStr) as OpenAIResponsesStreamEvent;
        } catch {
          continue;
        }

        if (event.type === "response.failed" || event.type === "error") {
          streamError = event.response?.error?.message || event.error?.message || "stream failed";
        }

        const delta = extractTextDelta(event);
        if (delta) onDelta(delta);
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // Reader may already be released after abort.
    }
  }

  if (streamError) {
    throw new Error(`Narrator stream error: ${streamError}`);
  }
}

export const streamNarratorResponse: NarratorStreamClient = async ({
  model,
  payload,
  signal,
  onDelta,
}) => {
  const timeout = createTimeoutSignal(signal);
  try {
    const response = await fetchWithRetry(
      buildApiUrl("/api/ai/openai/responses"),
      withCredentials({
        method: "POST",
        body: JSON.stringify({
          model,
          instructions: POWERPOINT_NARRATOR_STREAM_SYSTEM_PROMPT,
          input: JSON.stringify(payload),
          stream: true,
          store: false,
          reasoning: { effort: "low" },
          text: { format: { type: "text" } },
        }),
        signal: timeout.signal,
      }),
      1,
      "PPT-NARRATOR"
    );

    if (!response.ok) {
      throw new Error(await readModelError(response, "Narrator request failed"));
    }

    const contentType = response.headers.get("content-type") || "";
    if (response.body && contentType.includes("text/event-stream")) {
      await readResponsesTextStream(response.body, onDelta);
      return;
    }

    const json = (await response.json()) as { output_text?: string };
    if (typeof json.output_text === "string") {
      onDelta(json.output_text);
    }
  } finally {
    timeout.cleanup();
  }
};
